import React, { useMemo, useState } from 'react';
import { DSAccordion, DSAccordionItem, DSAlert, DSBackLink, DSBadge, DSBackgroundStage, DSButton, DSCard, DSCardFooterButton, DSCheckbox, DSFilterSelect, DSTypeaheadFilterSelect, DSIcon, DSInput, DSModal, DSPageHeader, DSRadio, DSSplitButton, DSSelect, DSToggle, DSTableShell, DSTextarea, DSToast, DSBottomNav } from '../design-system/primitives';
import { globalTokens, semanticTokens } from '../design-system/tokens/tokens';
import DeveloperBottomNav from '../components/DeveloperBottomNav';
import pingolfHero from '../assets/pingolf-hero.webp';
import './DesignSystemReference.css';

const snippets = {
  button: `<DSButton appearance="solid" tone="primary" leadingIcon={<DSIcon name="plus" size="sm" />}>Create Member</DSButton>\n<DSButton appearance="outline" tone="secondary" leadingIcon={<DSIcon name="edit" size="sm" />}>Edit Settings</DSButton>\n<DSButton appearance="text" tone="neutral">Learn More</DSButton>\n<DSButton appearance="solid" tone="primary" iconOnly aria-label="Add member" leadingIcon={<DSIcon name="plus" size="sm" />} />`,
  backLink: `<DSBackLink to="/events">Back to Events</DSBackLink>\n<DSBackLink to="/pingolf" tone="subtle" size="sm">Back to Events</DSBackLink>\n<DSBackLink to="/events" iconOnly>Back to Events</DSBackLink>\n<DSBackLink onClick={() => setStep(0)}>Back</DSBackLink>`,
  form: `<DSInput id="firstName" label="First Name" placeholder="Ada" />\n<DSSelect id="role" label="Role">\n  <option>Member</option>\n  <option>Admin</option>\n</DSSelect>\n<DSTextarea id="notes" label="Notes" rows={4} />`,
  controls: `<DSCheckbox id="notify" checked label="Email notifications" />\n<DSRadio id="modeA" name="mode" checked label="Mode A" />\n<DSToggle id="advanced" checked label="Advanced settings" />`,
  alerts: `<DSAlert variant="success">Settings saved successfully.</DSAlert>\n<DSAlert variant="success" appearance="outline">Settings saved successfully.</DSAlert>\n<DSBadge variant="warning" appearance="outline">Warning</DSBadge>\n<DSToast variant="info" appearance="outline">Host list refreshed</DSToast>`,
  table: `<DSTableShell\n  columns={["Name", "Role", "Status"]}\n  rows={[["Sam Lee", "Host", "Active"], ["Kim Patel", "Admin", "Pending"]]}\n/>`,
  bottomNav: `<DSBottomNav\n  ariaLabel="My membership"\n  items={[\n    { key: 'home', icon: 'home', label: 'Home', active: true, onClick: goHome },\n    { key: 'standing', icon: 'trophy', label: 'Standing', onClick: goStanding },\n    { key: 'profile', icon: 'user', label: 'Profile', onClick: goProfile },\n    { key: 'assigned', icon: 'clipboard', label: 'Assigned',\n      count: 12, countLabel: 'items waiting on you', onClick: goAssigned }\n  ]}\n  centerAction={{ icon: 'qr', label: 'Membership card', onClick: openCard }}\n/>`
};

const typographyColumns = [
  {
    label: 'Desktop',
    headings: semanticTokens.typography.heading.desktop,
    body: semanticTokens.typography.body.desktop
  },
  {
    label: 'Tablet',
    headings: semanticTokens.typography.heading.tablet,
    body: semanticTokens.typography.body.tablet
  },
  {
    label: 'Mobile',
    headings: semanticTokens.typography.heading.mobile,
    body: semanticTokens.typography.body.mobile
  }
];

const paletteSections = [
  {
    title: 'Brand Purple',
    tokens: ['purple50', 'purple100', 'purple200', 'purple300', 'purple400', 'purple500', 'purple600', 'purple700', 'purple800', 'purple900']
  },
  {
    title: 'Brand Gold',
    tokens: ['gold50', 'gold100', 'gold200', 'gold300', 'gold400', 'gold500', 'gold600', 'gold700', 'gold800', 'gold900']
  },
  {
    title: 'Neutrals',
    tokens: ['neutral0', 'neutral50', 'neutral100', 'neutral200', 'neutral300', 'neutral400', 'neutral500', 'neutral600', 'neutral700', 'neutral800', 'neutral900']
  },
  {
    title: 'Feedback',
    tokens: ['success100', 'success500', 'success700', 'warning100', 'warning500', 'warning700', 'error100', 'error500', 'error700', 'info100', 'info500', 'info700']
  }
];

const semanticSwatches = [
  ['surface', semanticTokens.color.surface],
  ['surfaceRaised', semanticTokens.color.surfaceRaised],
  ['surfaceBrandSubtle', semanticTokens.color.surfaceBrandSubtle],
  ['textDefault', semanticTokens.color.textDefault],
  ['textMuted', semanticTokens.color.textMuted],
  ['borderFocus', semanticTokens.color.borderFocus],
  ['statusSuccessBg', semanticTokens.color.statusSuccessBg],
  ['statusWarningBg', semanticTokens.color.statusWarningBg],
  ['statusErrorBg', semanticTokens.color.statusErrorBg],
  ['statusInfoBg', semanticTokens.color.statusInfoBg]
];

const buttonTones = ['primary', 'secondary', 'neutral', 'danger'];
const buttonAppearances = ['solid', 'outline', 'text'];
const buttonSizes = ['lg', 'md', 'sm', 'xs', 'xxs'];
const buttonStates = ['default', 'hover', 'focus', 'active', 'disabled'];
const fieldStates = ['inactive', 'hover', 'focus', 'disabled', 'validation', 'error', 'loading', 'success'];
const selectControlStates = ['default', 'hover', 'focus', 'disabled', 'error'];
const iconNames = [
  'user', 'users', 'trophy', 'swords', 'sparkles', 'golf', 'machine', 'wrench', 'question',
  'gamepad', 'gift', 'target', 'key', 'logout', 'card', 'dollar', 'plus', 'edit', 'search',
  'calendar', 'check', 'x', 'arrowLeft', 'arrowRight', 'chevronDown', 'chevronRight', 'info',
  'qr', 'home', 'clipboard', 'copy'
];

const tocItems = [
  { id: 'dsr-typography', label: 'Typography' },
  { id: 'dsr-color', label: 'Color & Background Modes' },
  { id: 'dsr-icons', label: 'Icons' },
  { id: 'dsr-buttons', label: 'Buttons' },
  { id: 'dsr-back-nav', label: 'Back Navigation' },
  { id: 'dsr-forms', label: 'Form Controls' },
  { id: 'dsr-select-controls', label: 'Select Controls' },
  { id: 'dsr-split', label: 'Split Buttons' },
  { id: 'dsr-accordion', label: 'Accordion' },
  { id: 'dsr-filters', label: 'Filter Selects' },
  { id: 'dsr-status', label: 'Status Components' },
  { id: 'dsr-table', label: 'Table Shell' },
  { id: 'dsr-cards', label: 'Cards' },
  { id: 'dsr-modals', label: 'Modals' },
  { id: 'dsr-bottom-nav', label: 'Bottom Navigation' },
  { id: 'dsr-docs', label: 'Related Docs' }
];

function formatTypeSpec(spec) {
  return `${spec.weight} / ${spec.size} / ${spec.lineHeight}`;
}

function Snippet({ code, title = 'Show code example' }) {
  return (
    <details className="dsr-snippet-disclosure">
      <summary>{title}</summary>
      <pre className="dsr-snippet" aria-label="Code snippet">
        <code>{code}</code>
      </pre>
    </details>
  );
}

function DesignSystemReference() {
  const [showModal, setShowModal] = useState(false);
  const [modalSize, setModalSize] = useState('md');
  const [selectedFilters, setSelectedFilters] = useState(['active']);
  const [eventTypeFilters, setEventTypeFilters] = useState([]);
  const [selectedHosts, setSelectedHosts] = useState([]);
  const [controlPlayground, setControlPlayground] = useState({
    checkbox: true,
    radio: 'mode-a',
    toggle: false
  });

  const hostDirectoryOptions = useMemo(
    () => Array.from({ length: 560 }, (_, index) => ({
      value: `host-${index + 1}`,
      label: `Host Candidate ${index + 1}`
    })),
    []
  );

  return (
    <div className="design-system-reference ds-stack bn-page-padding">
      <DSPageHeader
        title="Design System Reference"
        description="Canonical primitives, tokens, and usage patterns for AI-assisted and human-authored UI."
      />

      <div className="dsr-layout">
        <aside className="dsr-toc" aria-label="Design system table of contents">
          <h2>Contents</h2>
          <ul>
            {tocItems.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ul>
        </aside>

        <div className="dsr-content">

      <section id="dsr-typography">
      <DSCard title="Typography">
        <div className="dsr-font-card">
          <span className="dsr-kicker">Font Family</span>
          <div className="dsr-font-family">Nunito Sans</div>
        </div>
        <div className="dsr-typography-grid">
          {typographyColumns.map((column) => (
            <div key={column.label} className="dsr-typography-column">
              <h3 className="dsr-section-subtitle">{column.label}</h3>
              {Object.entries(column.headings).map(([level, spec]) => (
                <div key={level} className="dsr-type-sample">
                  <div
                    className="dsr-type-preview"
                    style={{
                      fontSize: spec.size,
                      lineHeight: spec.lineHeight,
                      fontWeight: spec.weight,
                      fontFamily: semanticTokens.font.display
                    }}
                  >
                    {level.toUpperCase()}
                  </div>
                  <div className="dsr-type-meta">{formatTypeSpec(spec)}</div>
                </div>
              ))}
              {Object.entries(column.body).map(([level, spec]) => (
                <div key={level} className="dsr-type-sample dsr-type-sample--body">
                  <div
                    className="dsr-type-preview"
                    style={{
                      fontSize: spec.size,
                      lineHeight: spec.lineHeight,
                      fontWeight: spec.weight,
                      fontFamily: semanticTokens.font.body
                    }}
                  >
                    {level.toUpperCase()}
                  </div>
                  <div className="dsr-type-meta">{formatTypeSpec(spec)}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="dsr-type-groups">
          <div>
            <h3 className="dsr-section-subtitle">Button / Link</h3>
            {Object.entries(semanticTokens.typography.button).map(([name, spec]) => (
              <div key={name} className="dsr-type-inline-row">
                <span className="dsr-type-inline-label">{name}</span>
                <span className="dsr-type-meta">{formatTypeSpec(spec)}</span>
              </div>
            ))}
          </div>
          <div>
            <h3 className="dsr-section-subtitle">Fields</h3>
            {Object.entries(semanticTokens.typography.field).map(([name, spec]) => (
              <div key={name} className="dsr-type-inline-row">
                <span className="dsr-type-inline-label">{name}</span>
                <span className="dsr-type-meta">{formatTypeSpec(spec)}</span>
              </div>
            ))}
          </div>
        </div>
      </DSCard>
      </section>

      <section id="dsr-color">
      <DSCard title="Color System">
        {paletteSections.map((section) => (
          <div key={section.title} className="dsr-palette-section">
            <h3 className="dsr-section-subtitle">{section.title}</h3>
            <div className="dsr-token-grid">
              {section.tokens.map((tokenName) => (
                <div key={tokenName} className="dsr-token-item">
                  <span className="dsr-token-name">{tokenName}</span>
                  <div className="dsr-token-swatch" style={{ background: globalTokens.color[tokenName] }} aria-label={`${tokenName} token swatch`} />
                  <span className="dsr-token-value">{globalTokens.color[tokenName]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="dsr-palette-section">
          <h3 className="dsr-section-subtitle">Semantic Surfaces & Status</h3>
          <div className="dsr-token-grid">
            {semanticSwatches.map(([name, value]) => (
              <div key={name} className="dsr-token-item">
                <span className="dsr-token-name">{name}</span>
                <div className="dsr-token-swatch" style={{ background: value }} aria-label={`${name} token swatch`} />
                <span className="dsr-token-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </DSCard>

      <DSCard title="Approved Background Modes" subtitle="Use one of these shell backgrounds and place content on approved surfaces">
        <div className="dsr-background-grid">
          <DSBackgroundStage mode="vivid" className="dsr-background-sample">
            <h3>Vivid</h3>
            <p>Default event/admin shell. Use white or muted surfaces for readable content blocks.</p>
          </DSBackgroundStage>
          <DSBackgroundStage mode="calm" className="dsr-background-sample">
            <h3>Calm</h3>
            <p>Reduced contrast shell for dense data and long scanning sessions.</p>
          </DSBackgroundStage>
          <DSBackgroundStage mode="contrast" className="dsr-background-sample">
            <h3>Contrast</h3>
            <p>High-contrast shell for utility-heavy pages where hierarchy must pop strongly.</p>
          </DSBackgroundStage>
        </div>
        <ul className="dsr-list">
          <li>Direct text on gradient is limited to page headers and utility metadata.</li>
          <li>Dense content must be placed on `surface`, `surfaceMuted`, or `surfaceOnVivid` cards.</li>
          <li>Do not mix custom page-level gradients with DS background modes in the same view.</li>
        </ul>
      </DSCard>
      </section>

      <section id="dsr-icons">
      <DSCard title="Icons" subtitle="Canonical icon sizes and tones for app chrome and states">
        <div className="dsr-icon-size-group">
          <h3 className="dsr-section-subtitle">Size 16</h3>
          <div className="dsr-icon-grid">
            {iconNames.map((name) => <DSIcon key={`sm-${name}`} name={name} size="sm" tone="default" label={name} />)}
          </div>
        </div>
        <div className="dsr-icon-size-group">
          <h3 className="dsr-section-subtitle">Size 24</h3>
          <div className="dsr-icon-grid">
            {iconNames.map((name) => <DSIcon key={`md-${name}`} name={name} size="md" tone="brand" label={name} />)}
          </div>
        </div>
        <div className="dsr-icon-size-group">
          <h3 className="dsr-section-subtitle">Size 32</h3>
          <div className="dsr-icon-grid">
            {iconNames.map((name) => <DSIcon key={`lg-${name}`} name={name} size="lg" tone="muted" label={name} />)}
          </div>
        </div>
        <div className="dsr-row">
          <DSBadge variant="info" appearance="outline">Policy</DSBadge>
          <p className="dsr-text">Emoji are not allowed in UI chrome. Use DSIcon names and tone variants (`default`, `muted`, `brand`, `inverse`, `success`, `warning`, `danger`, `info`).</p>
        </div>
      </DSCard>
      </section>

      <section id="dsr-buttons">
      <DSCard title="Buttons">
        <div className="dsr-button-matrix">
          <div className="dsr-button-matrix-header">Tone</div>
          {buttonAppearances.map((appearance) => (
            <div key={appearance} className="dsr-button-matrix-header">{appearance}</div>
          ))}
          {buttonTones.flatMap((tone) => [
            <div key={`${tone}-label`} className="dsr-button-tone-label">{tone}</div>,
            ...buttonAppearances.map((appearance) => (
              <div key={`${tone}-${appearance}`} className="dsr-button-cell">
                <DSButton appearance={appearance} tone={tone}>{tone}</DSButton>
              </div>
            ))
          ])}
        </div>

        <div className="dsr-button-patterns">
          <div>
            <h3 className="dsr-section-subtitle">Icon + Text</h3>
            <div className="dsr-row">
              <DSButton appearance="solid" tone="primary" leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}>Create Member</DSButton>
              <DSButton appearance="outline" tone="secondary" leadingIcon={<DSIcon name="edit" size="sm" tone="brand" />}>Edit Entry</DSButton>
              <DSButton appearance="text" tone="neutral" trailingIcon={<DSIcon name="arrowRight" size="sm" tone="default" />}>View Details</DSButton>
            </div>
          </div>
          <div>
            <h3 className="dsr-section-subtitle">Icon Only</h3>
            <div className="dsr-row">
              <DSButton appearance="solid" tone="primary" size="lg" iconOnly aria-label="Add member" leadingIcon={<DSIcon name="plus" size="sm" tone="default" />} />
              <DSButton appearance="outline" tone="secondary" size="md" iconOnly aria-label="Edit member" leadingIcon={<DSIcon name="edit" size="sm" tone="brand" />} />
              <DSButton appearance="text" tone="neutral" size="sm" iconOnly aria-label="Open details" leadingIcon={<DSIcon name="arrowRight" size="sm" tone="default" />} />
              <DSButton appearance="solid" tone="danger" size="xs" iconOnly aria-label="Delete member" leadingIcon={<DSIcon name="x" size="sm" tone="default" />} />
            </div>
          </div>
          <div>
            <h3 className="dsr-section-subtitle">Size Scale</h3>
            <div className="dsr-row">
              <DSButton appearance="solid" tone="primary" size="lg">Large</DSButton>
              <DSButton appearance="solid" tone="primary" size="md">Medium</DSButton>
              <DSButton appearance="solid" tone="primary" size="sm">Small</DSButton>
              <DSButton appearance="solid" tone="primary" size="xs">X-Small</DSButton>
              <DSButton appearance="solid" tone="primary" size="xxs">XX-Small</DSButton>
            </div>
          </div>
        </div>

        <div className="dsr-button-state-gallery">
          <h3 className="dsr-section-subtitle">Primary Solid: Variant x State x Size</h3>
          <div className="dsr-button-states-grid">
            <div className="dsr-button-states-cell dsr-button-states-cell--header">Size</div>
            {buttonStates.map((state) => (
              <div key={`header-${state}`} className="dsr-button-states-cell dsr-button-states-cell--header">{state}</div>
            ))}
            {buttonSizes.flatMap((size) => [
              <div key={`size-${size}`} className="dsr-button-states-cell dsr-button-states-cell--size">{size}</div>,
              ...buttonStates.map((state) => (
                <div key={`${size}-${state}`} className="dsr-button-states-cell">
                  <DSButton appearance="solid" tone="primary" size={size} state={state}>
                    Label
                  </DSButton>
                </div>
              ))
            ])}
          </div>

          <h3 className="dsr-section-subtitle">Secondary Outline: Icon Patterns Across States</h3>
          <div className="dsr-button-states-grid dsr-button-states-grid--dense">
            <div className="dsr-button-states-cell dsr-button-states-cell--header">Pattern</div>
            {buttonStates.map((state) => (
              <div key={`pattern-header-${state}`} className="dsr-button-states-cell dsr-button-states-cell--header">{state}</div>
            ))}

            {['text-only', 'icon-left', 'icon-right', 'icon-only'].flatMap((pattern) => [
              <div key={`pattern-${pattern}`} className="dsr-button-states-cell dsr-button-states-cell--size">{pattern}</div>,
              ...buttonStates.map((state) => (
                <div key={`${pattern}-${state}`} className="dsr-button-states-cell">
                  {pattern === 'text-only' && (
                    <DSButton appearance="outline" tone="secondary" size="sm" state={state}>Label</DSButton>
                  )}
                  {pattern === 'icon-left' && (
                    <DSButton appearance="outline" tone="secondary" size="sm" state={state} leadingIcon={<DSIcon name="plus" size="sm" tone="brand" />}>Label</DSButton>
                  )}
                  {pattern === 'icon-right' && (
                    <DSButton appearance="outline" tone="secondary" size="sm" state={state} trailingIcon={<DSIcon name="arrowRight" size="sm" tone="brand" />}>Label</DSButton>
                  )}
                  {pattern === 'icon-only' && (
                    <DSButton appearance="outline" tone="secondary" size="sm" state={state} iconOnly leadingIcon={<DSIcon name="edit" size="sm" tone="brand" />} aria-label="Edit" />
                  )}
                </div>
              ))
            ])}
          </div>
        </div>
        <Snippet code={snippets.button} />
      </DSCard>
      </section>

      <section id="dsr-back-nav">
      <DSCard title="Back Navigation" subtitle="DSBackLink is the only back control in the app - no page should hand-roll one">
        <p className="dsr-bnav-intro">
          Chromeless white by default, carrying its own shadow so it stays legible over
          hero photography without a pill behind it. Name the destination rather than
          saying just &quot;Back&quot;, and prefer a real <code>to</code> route over
          history-back so deep links and post-redirect loads still work.
        </p>

        <h3 className="dsr-section-subtitle">Over hero photography</h3>
        <div className="dsr-bnav-samples">
          <div className="dsr-bnav-cell dsr-bnav-cell--scrim" style={{ backgroundImage: `url(${pingolfHero})` }}>
            <div className="dsr-bnav-cell-inner">
              <DSBackLink to="/developer">All seasons</DSBackLink>
              <p className="dsr-bnav-faux-title">With scrim</p>
            </div>
          </div>
          <div className="dsr-bnav-cell" style={{ backgroundImage: `url(${pingolfHero})` }}>
            <div className="dsr-bnav-cell-inner">
              <DSBackLink to="/developer">All seasons</DSBackLink>
              <p className="dsr-bnav-faux-title">No scrim</p>
            </div>
          </div>
        </div>

        <h3 className="dsr-section-subtitle">On a flat surface</h3>
        <div className="dsr-row">
          <DSBackLink to="/developer">Back to Developer Tools</DSBackLink>
          <DSBackLink to="/pingolf" tone="subtle" size="sm">Back to Events</DSBackLink>
        </div>

        <h3 className="dsr-section-subtitle">Sizes</h3>
        <div className="dsr-row">
          <DSBackLink to="/developer" size="sm">Back (sm)</DSBackLink>
          <DSBackLink to="/developer" size="md">Back (md, default)</DSBackLink>
          <DSBackLink to="/developer" size="lg">Back (lg)</DSBackLink>
        </div>

        <h3 className="dsr-section-subtitle">Icon only</h3>
        <p className="dsr-bnav-intro">
          For headers too tight for a destination label. The label stays in the markup
          and is clipped, so screen readers still announce it and there is no aria-label
          to remember.
        </p>
        <div className="dsr-row">
          <DSBackLink to="/developer" iconOnly>Back to Events</DSBackLink>
          <DSBackLink to="/developer" tone="subtle" iconOnly>Back to Events</DSBackLink>
        </div>

        <Snippet code={snippets.backLink} />
      </DSCard>
      </section>

      <section id="dsr-forms">
      <DSCard title="Form Controls">
        <div className="dsr-form-grid">
          <DSInput id="dsr-input-name" label="Display Name" placeholder="Enter display name" helpText="Required for profile header" />
          <DSSelect id="dsr-select-role" label="Role">
            <option>Member</option>
            <option>Host</option>
            <option>Admin</option>
          </DSSelect>
          <DSTextarea id="dsr-textarea-notes" label="Notes" rows={4} placeholder="Add a short note" />
        </div>
        <Snippet code={snippets.form} />

        <h3 className="dsr-section-subtitle">Field State Matrix</h3>
        <div className="dsr-field-state-matrix">
          <div className="dsr-field-state-header">Field Type</div>
          {fieldStates.map((state) => (
            <div key={`field-header-${state}`} className="dsr-field-state-header">{state}</div>
          ))}

          <div className="dsr-field-state-label">Input</div>
          {fieldStates.map((state) => (
            <div key={`input-${state}`} className="dsr-field-state-cell">
              <DSInput id={`field-input-${state}`} state={state} placeholder="Text" helpText={state === 'error' ? 'Error' : ''} />
            </div>
          ))}

          <div className="dsr-field-state-label">Input + Icon</div>
          {fieldStates.map((state) => (
            <div key={`input-icon-${state}`} className="dsr-field-state-cell">
              <DSInput id={`field-input-icon-${state}`} state={state} placeholder="Text" trailingIcon={<DSIcon name="calendar" />} helpText={state === 'validation' ? 'Warning' : ''} />
            </div>
          ))}

          <div className="dsr-field-state-label">Dropdown</div>
          {fieldStates.map((state) => (
            <div key={`dropdown-${state}`} className="dsr-field-state-cell">
              <DSSelect id={`field-select-${state}`} state={state}>
                <option>Text</option>
              </DSSelect>
            </div>
          ))}

          <div className="dsr-field-state-label">Text Area</div>
          {fieldStates.map((state) => (
            <div key={`textarea-${state}`} className="dsr-field-state-cell">
              <DSTextarea id={`field-textarea-${state}`} state={state} rows={3} placeholder="Text" />
            </div>
          ))}
        </div>
      </DSCard>
      </section>

      <section id="dsr-select-controls">
      <DSCard title="Select Controls">
        <p className="dsr-text">Interactive playground:</p>
        <div className="dsr-controls-playground">
          <DSCheckbox
            id="controls-playground-checkbox"
            label="Email notifications"
            checked={controlPlayground.checkbox}
            onChange={(event) => setControlPlayground((prev) => ({ ...prev, checkbox: event.target.checked }))}
          />
          <DSRadio
            id="controls-playground-radio-a"
            name="controls-playground-radio"
            label="Mode A"
            checked={controlPlayground.radio === 'mode-a'}
            onChange={() => setControlPlayground((prev) => ({ ...prev, radio: 'mode-a' }))}
          />
          <DSRadio
            id="controls-playground-radio-b"
            name="controls-playground-radio"
            label="Mode B"
            checked={controlPlayground.radio === 'mode-b'}
            onChange={() => setControlPlayground((prev) => ({ ...prev, radio: 'mode-b' }))}
          />
          <DSToggle
            id="controls-playground-toggle"
            label="Advanced settings"
            checked={controlPlayground.toggle}
            onChange={(event) => setControlPlayground((prev) => ({ ...prev, toggle: event.target.checked }))}
          />
        </div>

        <h3 className="dsr-section-subtitle">State Matrix (visual reference)</h3>
        <h3 className="dsr-section-subtitle">Checkbox</h3>
        <div className="dsr-select-control-grid">
          <div className="dsr-select-control-header">State</div>
          {selectControlStates.map((state) => <div key={`cb-head-${state}`} className="dsr-select-control-header">{state}</div>)}
          <div className="dsr-select-control-label">Off</div>
          {selectControlStates.map((state) => (
            <div key={`cb-off-${state}`} className="dsr-select-control-cell">
              <DSCheckbox id={`cb-off-${state}`} state={state} checked={false} readOnly />
            </div>
          ))}
          <div className="dsr-select-control-label">On</div>
          {selectControlStates.map((state) => (
            <div key={`cb-on-${state}`} className="dsr-select-control-cell">
              <DSCheckbox id={`cb-on-${state}`} state={state} checked readOnly />
            </div>
          ))}
        </div>

        <h3 className="dsr-section-subtitle">Radio Button</h3>
        <div className="dsr-select-control-grid">
          <div className="dsr-select-control-header">State</div>
          {selectControlStates.map((state) => <div key={`rb-head-${state}`} className="dsr-select-control-header">{state}</div>)}
          <div className="dsr-select-control-label">Off</div>
          {selectControlStates.map((state) => (
            <div key={`rb-off-${state}`} className="dsr-select-control-cell">
              <DSRadio id={`rb-off-${state}`} name={`rb-off-group-${state}`} state={state} checked={false} readOnly />
            </div>
          ))}
          <div className="dsr-select-control-label">On</div>
          {selectControlStates.map((state) => (
            <div key={`rb-on-${state}`} className="dsr-select-control-cell">
              <DSRadio id={`rb-on-${state}`} name={`rb-on-group-${state}`} state={state} checked readOnly />
            </div>
          ))}
        </div>

        <h3 className="dsr-section-subtitle">Toggle</h3>
        <div className="dsr-select-control-grid dsr-select-control-grid--toggle">
          <div className="dsr-select-control-header">State</div>
          {['default', 'hover', 'focus', 'disabled'].map((state) => <div key={`tg-head-${state}`} className="dsr-select-control-header">{state}</div>)}
          <div className="dsr-select-control-label">Off</div>
          {['default', 'hover', 'focus', 'disabled'].map((state) => (
            <div key={`tg-off-${state}`} className="dsr-select-control-cell">
              <DSToggle id={`tg-off-${state}`} state={state} checked={false} readOnly />
            </div>
          ))}
          <div className="dsr-select-control-label">On</div>
          {['default', 'hover', 'focus', 'disabled'].map((state) => (
            <div key={`tg-on-${state}`} className="dsr-select-control-cell">
              <DSToggle id={`tg-on-${state}`} state={state} checked readOnly />
            </div>
          ))}
        </div>

        <Snippet code={snippets.controls} />
      </DSCard>
      </section>

      <section id="dsr-split">
      <DSCard title="Split Buttons">
        <p className="dsr-text">Use split buttons for primary actions with adjacent secondary actions.</p>
        <div className="dsr-row">
          <DSSplitButton
            label="Save Changes"
            tone="primary"
            appearance="solid"
            actions={[
              { label: 'Save Draft', onClick: () => {} },
              { label: 'Save and Publish', onClick: () => {} },
              { label: 'Delete', tone: 'danger', onClick: () => {} }
            ]}
            onMainClick={() => {}}
          />
          <DSSplitButton
            label="Assign Host"
            tone="secondary"
            appearance="outline"
            size="sm"
            actions={[
              { label: 'Assign as Lead Host', onClick: () => {} },
              { label: 'Assign as Backup', onClick: () => {} },
              { label: 'Remove Assignment', tone: 'danger', onClick: () => {} }
            ]}
            onMainClick={() => {}}
          />
        </div>
      </DSCard>
      </section>

      <section id="dsr-accordion">
      <DSCard title="Accordion" subtitle="Use for dense sections and optional details">
        <DSAccordion>
          <DSAccordionItem title="Hole Rules" subtitle="Show guidance only when needed" defaultOpen>
            <p className="dsr-text">Accordion content reduces vertical sprawl while keeping advanced details available.</p>
          </DSAccordionItem>
          <DSAccordionItem title="Lifecycle Notes" subtitle="Status transitions and side effects">
            <p className="dsr-text">Keep action explanations close to the controls they affect.</p>
          </DSAccordionItem>
        </DSAccordion>
      </DSCard>
      </section>

      <section id="dsr-filters">
      <DSCard title="Filter Selects">
        <p className="dsr-text">Filter selects support chip summaries and quick clear behavior for faceted filtering flows.</p>
        <div className="dsr-form-grid">
          <DSFilterSelect
            id="status-filters"
            label="Status Filters"
            selectedValues={selectedFilters}
            onChange={setSelectedFilters}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Pending', value: 'pending' },
              { label: 'Paused', value: 'paused' },
              { label: 'Archived', value: 'archived' }
            ]}
          />
          <DSFilterSelect
            id="event-filters"
            label="Event Types"
            selectedValues={eventTypeFilters}
            onChange={setEventTypeFilters}
            options={[
              { label: 'Pingolf', value: 'pingolf' },
              { label: 'Tournament', value: 'tournament' },
              { label: 'Book Club', value: 'book-club' },
              { label: 'Video Game Night', value: 'video-game-night' }
            ]}
            placeholder="Choose event types"
          />
          <DSTypeaheadFilterSelect
            id="host-filters"
            label="Host Search (Typeahead)"
            selectedValues={selectedHosts}
            onChange={setSelectedHosts}
            options={hostDirectoryOptions}
            placeholder="Find hosts in 500+ directory"
            maxVisibleOptions={40}
          />
        </div>
      </DSCard>
      </section>

      <section id="dsr-status">
      <DSCard title="Alerts, Badges, Toasts">
        <h3 className="dsr-section-subtitle">Solid</h3>
        <div className="dsr-stack-inline">
          <DSAlert variant="success">Success alert</DSAlert>
          <DSAlert variant="warning">Warning alert</DSAlert>
          <DSAlert variant="error">Error alert</DSAlert>
          <DSAlert variant="info">Info alert</DSAlert>
        </div>
        <div className="dsr-row">
          <DSBadge variant="success">Success</DSBadge>
          <DSBadge variant="warning">Warning</DSBadge>
          <DSBadge variant="error">Error</DSBadge>
          <DSBadge variant="info">Info</DSBadge>
        </div>

        <h3 className="dsr-section-subtitle">Outline</h3>
        <div className="dsr-stack-inline">
          <DSAlert variant="success" appearance="outline">Success alert</DSAlert>
          <DSAlert variant="warning" appearance="outline">Warning alert</DSAlert>
          <DSAlert variant="error" appearance="outline">Error alert</DSAlert>
          <DSAlert variant="info" appearance="outline">Info alert</DSAlert>
        </div>
        <div className="dsr-row">
          <DSBadge variant="success" appearance="outline">Success</DSBadge>
          <DSBadge variant="warning" appearance="outline">Warning</DSBadge>
          <DSBadge variant="error" appearance="outline">Error</DSBadge>
          <DSBadge variant="info" appearance="outline">Info</DSBadge>
        </div>

        <div className="dsr-toast-preview">
          <DSToast variant="success">Saved host assignment.</DSToast>
          <DSToast variant="info" appearance="outline">Host list refreshed.</DSToast>
        </div>
        <Snippet code={snippets.alerts} />
      </DSCard>
      </section>

      <section id="dsr-table">
      <DSCard title="Table Shell">
        <DSTableShell
          columns={['Name', 'Role', 'Status']}
          rows={[
            ['Jordan Riley', 'Host', 'Active'],
            ['Taylor Quinn', 'Admin', 'Pending'],
            ['Drew Morgan', 'Member', 'Active']
          ]}
        />
        <Snippet code={snippets.table} />
      </DSCard>
      </section>

      <section id="dsr-cards">
      <DSCard title="Cards">
        <p className="dsr-text">Cards now support independent background, border, and corner variants to cover real application needs.</p>
        <div className="dsr-row">
          <DSCard title="Elevated / Default" variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg">
            <p className="dsr-text">Default card with subtle elevation and rounded corners.</p>
          </DSCard>
          <DSCard title="Outlined / Strong Border" variant="outlined" bgVariant="subtle" borderVariant="strong" cornerVariant="md">
            <p className="dsr-text">Dense layout card with strong border and compact radius.</p>
          </DSCard>
          <DSCard title="Tonal / Accent Border" variant="tonal" bgVariant="brand" borderVariant="accent" cornerVariant="xl">
            <p className="dsr-text">Callout card using brand background and accent border.</p>
          </DSCard>
          <DSCard title="Status Card" variant="outlined" bgVariant="success" borderVariant="success" cornerVariant="sm">
            <p className="dsr-text">Status-specific card variant for success messaging.</p>
          </DSCard>
        </div>

        <h3 className="dsr-section-subtitle">Header and footer</h3>
        <p className="dsr-text">
          A card is a header over a body over a footer, and all three are the card's job. Pass
          <code> title</code>, <code>subtitle</code> and <code>actions</code> for the header and
          <code> footer</code> for the footer. Do not hand-roll either inside <code>children</code>:
          that is how the app ended up with the same bracketed card written twenty times.
        </p>
        <p className="dsr-text">
          The two frames differ in where the padding lives. <code>frame="inset"</code> (the
          default) keeps it on the card, so the footer contributes only the rule that separates
          it from the body. <code>frame="bars"</code> moves it into the body, so the header and
          footer become full-bleed strips. Add <code>flush</code> in the bars frame when the body
          is a list of rows: it drops the body element so the row separators reach the card
          edges, which is what makes a list read as a list rather than as stacked paragraphs.
        </p>
        <div className="dsr-row">
          <DSCard
            title="Open issues"
            subtitle="Across every machine on the floor"
            actions={<DSButton appearance="outline" tone="neutral" size="sm">Filter</DSButton>}
            footer={<DSButton appearance="text" tone="neutral" size="sm">View all 14</DSButton>}
          >
            <p className="dsr-text">frame="inset" - the default. The footer is marked off by a rule.</p>
          </DSCard>

          <DSCard
            frame="bars"
            density="compact"
            title="Recent transactions"
            subtitle="Last 30 days"
            footer={<DSCardFooterButton onClick={() => {}}>View all 6 transactions</DSCardFooterButton>}
          >
            <p className="dsr-text">frame="bars" - header and footer are strips on their own surface.</p>
          </DSCard>

          <DSCard frame="bars" flush density="compact" title="Machines" subtitle="Assigned to you">
            <ul className="dsr-card-rows">
              <li>Press 04</li>
              <li>Press 11</li>
              <li>Press 12</li>
            </ul>
          </DSCard>
        </div>
        <p className="dsr-text">
          <code>density="compact"</code> sets the title as a label rather than a display heading,
          for a header that names the rows under it instead of titling a section.
          <code> DSCardFooterButton</code> is the full-width bar that closes a bars card. It is
          deliberately not a <code>DSButton</code>: it is a region of the card, not a control
          sitting inside one, and a button's own box inside the bar reads as a button in a bar
          rather than as a bar you can press.
        </p>
        <Snippet code={`<DSCard
  frame="bars"
  flush
  density="compact"
  title="Recent transactions"
  subtitle="Last 30 days"
  footer={<DSCardFooterButton onClick={openAll}>View all 6 transactions</DSCardFooterButton>}
>
  {rows}
</DSCard>`} />
      </DSCard>
      </section>

      <section id="dsr-modals">
      <DSCard title="Modals">
        <p className="dsr-text">Modal definitions are now isolated from card definitions and include explicit `sm`, `md`, and `lg` sizes.</p>
        <div className="dsr-row">
          <DSButton appearance="solid" tone="primary" onClick={() => { setModalSize('sm'); setShowModal(true); }}>
            Open Small Modal
          </DSButton>
          <DSButton appearance="outline" tone="secondary" onClick={() => { setModalSize('md'); setShowModal(true); }}>
            Open Medium Modal
          </DSButton>
          <DSButton appearance="text" tone="neutral" onClick={() => { setModalSize('lg'); setShowModal(true); }}>
            Open Large Modal
          </DSButton>
        </div>
        {showModal && (
          <DSModal title="Confirm change" size={modalSize} onClose={() => setShowModal(false)}>
            <p className="dsr-text">This modal demonstrates default accessibility behavior and style tokens.</p>
            <div className="dsr-row">
              <DSButton appearance="outline" tone="neutral" onClick={() => setShowModal(false)}>Cancel</DSButton>
              <DSButton appearance="solid" tone="primary" onClick={() => setShowModal(false)}>Confirm</DSButton>
            </div>
          </DSModal>
        )}
      </DSCard>
      </section>

      <section id="dsr-bottom-nav">
      <DSCard
        title="Bottom Navigation"
        subtitle="Fixed tab bar for member-facing surfaces, with an optional floating centre action"
      >
        <p className="dsr-text">
          Items are data, not children, so every bar in the app is the same construction. The
          active item is marked three ways at once (brighter colour, heavier label, solid icon
          variant), because colour alone does not survive a colour-vision difference.
        </p>
        <p className="dsr-text">
          <code>centerAction</code> reserves a column and floats a circular action above the
          bar's top edge. It is not a tab. Use it for the one object on the surface that has to
          be reachable from every tab in a single tap, and give it no label. Counts use
          DSCountIndicator's brand tone rather than the default danger red: a count here is work
          waiting, not an alarm.
        </p>
        <p className="dsr-text">
          The bar is fixed, so the page that renders one needs the
          <code> ds-bottom-nav-page </code> class to reserve room for it.
        </p>
        <div className="dsr-bottom-nav-demo">
          <DSBottomNav
            ariaLabel="Bottom navigation example"
            items={[
              { key: 'home', icon: 'home', label: 'Home', active: true, onClick: () => {} },
              { key: 'standing', icon: 'trophy', label: 'Standing', onClick: () => {} },
              { key: 'profile', icon: 'user', label: 'Profile', onClick: () => {} },
              { key: 'assigned', icon: 'clipboard', label: 'Assigned', count: 12, countLabel: 'items waiting on you', onClick: () => {} }
            ]}
            centerAction={{ icon: 'qr', label: 'Membership card', onClick: () => {} }}
          />
        </div>
        <Snippet code={snippets.bottomNav} />
      </DSCard>
      </section>

      <section id="dsr-docs">
      <DSCard title="Related Docs">
        <ul className="dsr-list">
          <li><a href="/docs/DESIGN_SYSTEM_AUDIT.md" target="_blank" rel="noreferrer">Design System Audit (Markdown)</a></li>
          <li><a href="/docs/AI_UI_GENERATION_CONTRACT.md" target="_blank" rel="noreferrer">AI UI Generation Contract</a></li>
        </ul>
      </DSCard>
      </section>
        </div>
      </div>

      <DeveloperBottomNav />
    </div>
  );
}

export default DesignSystemReference;
